import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { apiRequest } from "../../common/api/apiClient";
import { uploadImage } from "../../tierApi/supabase";
import type {
  Rating,
  WorkPlace,
  WorkPlaceApiResponse,
  WorkPlaceType,
} from "../../types/entities";

// --- Types ---

interface WorkPlacesState {
  workPlaces: Record<WorkPlaceType, Record<string, WorkPlace>>;
  isLoadingWorkPlaces: boolean;
  failedToLoadWorkPlaces: boolean;
  isLoadingWorkPlaceCreation: boolean;
  failedToCreateWorkPlace: boolean;
  isLoadingRatingSubmission: boolean;
  failedToSubmitRating: boolean;
}

interface CreateWorkPlacePayload {
  name: string;
  type: WorkPlaceType;
  spotId: string;
  address: string;
  rating: number;
  comment: string;
  googleId: string;
  longitude: number;
  latitude: number;
  selectedFile: File | null;
}

interface SubmitRatingPayload {
  type: WorkPlaceType;
  workPlaceId: string;
  rating: number;
  comment: string;
  isEditMode?: boolean;
}

// --- Helpers ---

const createWorkPlaceObject = (place: WorkPlaceApiResponse): WorkPlace => {
  return {
    id: place.id,
    name: place.name,
    type: place.type,
    spotId: place.spot_id,
    submittedBy: place.submitted_by,
    creatorName: place.creator_name,
    address: place.address,
    image_link: place.image_link,
    longitude: parseFloat(place.longitude) || 0,
    latitude: parseFloat(place.latitude) || 0,
    totalRatings: parseInt(place.total_ratings, 10) || 0,
    averageRating: parseFloat(place.average_rating) || 0,
    ratings: place.ratings || [],
  };
};

// --- Async Thunks ---

export const createWorkPlace = createAsyncThunk(
  "workPlaces/createWorkPlace",
  async (
    workPlaceData: CreateWorkPlacePayload,
    { getState }: { getState: () => any },
  ) => {
    const {
      name,
      type,
      spotId,
      address,
      rating,
      comment,
      googleId,
      longitude,
      latitude,
      selectedFile,
    } = workPlaceData;

    let image_link: string | null = null;

    if (selectedFile) {
      try {
        image_link = await uploadImage(selectedFile, "workplace", name, type);
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    }

    const data = {
      id: googleId,
      name: name,
      type: type,
      spot_id: parseInt(spotId),
      image_link,
      address: address,
      rating: rating,
      comment: comment,
      longitude: longitude,
      latitude: latitude,
    };

    const token = getState().user.session?.access_token;
    const responseData = await apiRequest<WorkPlaceApiResponse>("/workplaces", {
      method: "POST",
      body: data,
      token,
    });

    return createWorkPlaceObject(responseData);
  },
);

export const loadWorkPlaces = createAsyncThunk(
  "workPlaces/loadWorkPlaces",
  async (spotId: number | string) => {
    const json = await apiRequest<WorkPlaceApiResponse[]>(
      `/spots/${spotId}/workplaces`,
    );

    const workPlacesData = json.reduce(
      (workPlaces: Record<WorkPlaceType, Record<string, WorkPlace>>, place) => {
        const type = place.type;
        const workPlace = createWorkPlaceObject(place);

        if (type === "coworking") {
          workPlaces.coworking[place.id] = workPlace;
        } else if (type === "café") {
          workPlaces.café[place.id] = workPlace;
        } else if (type === "coliving") {
          workPlaces.coliving[place.id] = workPlace;
        }

        return workPlaces;
      },
      { coworking: {}, café: {}, coliving: {} },
    );

    return workPlacesData;
  },
);

export const submitWorkPlaceRating = createAsyncThunk(
  "workPlaces/submitWorkPlaceRating",
  async (
    ratingData: SubmitRatingPayload,
    { getState }: { getState: () => any },
  ) => {
    const {
      type,
      workPlaceId,
      rating,
      comment,
      isEditMode = false,
    } = ratingData;

    const data = {
      type: type,
      work_place_id: workPlaceId,
      rating: rating,
      comment: comment,
    };

    const token = getState().user.session?.access_token;
    const responseData = await apiRequest<Rating>(
      `/workplaces/${workPlaceId}/ratings`,
      {
        method: isEditMode ? "PUT" : "POST",
        body: data,
        token,
      },
    );

    return { type, workPlaceId, rating: responseData, isEditMode };
  },
);

// --- Slice ---

const initialState: WorkPlacesState = {
  workPlaces: { coworking: {}, café: {}, coliving: {} },
  isLoadingWorkPlaces: false,
  failedToLoadWorkPlaces: false,
  isLoadingWorkPlaceCreation: false,
  failedToCreateWorkPlace: false,
  isLoadingRatingSubmission: false,
  failedToSubmitRating: false,
};

export const workPlacesSlice = createSlice({
  name: "workPlaces",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadWorkPlaces.pending, (state) => {
        state.isLoadingWorkPlaces = true;
        state.failedToLoadWorkPlaces = false;
        state.workPlaces = { coworking: {}, café: {}, coliving: {} };
      })
      .addCase(loadWorkPlaces.rejected, (state) => {
        state.isLoadingWorkPlaces = false;
        state.failedToLoadWorkPlaces = true;
      })
      .addCase(loadWorkPlaces.fulfilled, (state, action) => {
        state.isLoadingWorkPlaces = false;
        state.failedToLoadWorkPlaces = false;
        state.workPlaces = action.payload;
      })
      .addCase(createWorkPlace.pending, (state) => {
        state.isLoadingWorkPlaceCreation = true;
        state.failedToCreateWorkPlace = false;
      })
      .addCase(createWorkPlace.rejected, (state) => {
        state.isLoadingWorkPlaceCreation = false;
        state.failedToCreateWorkPlace = true;
      })
      .addCase(createWorkPlace.fulfilled, (state, action) => {
        state.isLoadingWorkPlaceCreation = false;
        state.failedToCreateWorkPlace = false;
        state.workPlaces[action.payload.type][action.payload.id] =
          action.payload;
      })
      .addCase(submitWorkPlaceRating.pending, (state) => {
        state.isLoadingRatingSubmission = true;
        state.failedToSubmitRating = false;
      })
      .addCase(submitWorkPlaceRating.rejected, (state) => {
        state.isLoadingRatingSubmission = false;
        state.failedToSubmitRating = true;
      })
      .addCase(submitWorkPlaceRating.fulfilled, (state, action) => {
        state.isLoadingRatingSubmission = false;
        state.failedToSubmitRating = false;
        const { type, workPlaceId, rating, isEditMode } = action.payload;
        const workPlace = state.workPlaces[type]?.[workPlaceId];
        if (!workPlace) return;

        const ratingIndex = isEditMode
          ? workPlace.ratings.findIndex((r) => r.user_id === rating.user_id)
          : -1;

        if (ratingIndex !== -1) {
          workPlace.ratings[ratingIndex] = rating;
        } else {
          workPlace.ratings.push(rating);
        }

        workPlace.totalRatings = workPlace.ratings.length;

        const sum = workPlace.ratings.reduce((acc, r) => acc + r.rating, 0);
        workPlace.averageRating =
          workPlace.ratings.length > 0 ? sum / workPlace.ratings.length : 0;
      });
  },
});

// Selectors — use `any` for state until RootState is available
export const selectWorkPlaces = (state: any) => state.workPlaces.workPlaces;

export const selectWorkPlacesArrayByType = createSelector(
  [selectWorkPlaces, (_state: any, type: WorkPlaceType) => type],
  (workPlaces, type) => {
    return Object.entries(workPlaces?.[type] || {})
      .map(([, workplace]) => workplace as WorkPlace)
      .sort((a, b) => b.averageRating - a.averageRating);
  },
);

export const selectWorkPlaceById = createSelector(
  [
    selectWorkPlaces,
    (_state: any, type: WorkPlaceType) => type,
    (_state: any, _type: WorkPlaceType, id: string) => id,
  ],
  (workPlaces, type, id): WorkPlace | null => {
    return workPlaces?.[type]?.[id] || null;
  },
);

export const selectAllWorkPlacesForMap = createSelector(
  [selectWorkPlaces],
  (workPlaces) => {
    const categories: WorkPlaceType[] = ["café", "coworking", "coliving"];
    return categories.reduce(
      (acc, category) => {
        acc[category] = workPlaces?.[category] || {};
        return acc;
      },
      {} as Record<WorkPlaceType, Record<string, WorkPlace>>,
    );
  },
);

export const failedToLoadWorkPlaces = (state: any): boolean =>
  state.workPlaces.failedToLoadWorkPlaces;
export const isLoadingWorkPlaces = (state: any): boolean =>
  state.workPlaces.isLoadingWorkPlaces;

export default workPlacesSlice.reducer;
